import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  ArrowUpRight,
  Check,
  ChevronLeft,
  KeyRound,
  Loader2,
  Shield,
} from "lucide-react";
import { IntegrationLogo } from "@/components/integrations/logo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { credentialsApi } from "@/lib/credentials-api";
import { authLabel, integrationsApi, type ToolTag } from "@/lib/integrations-api";

const TAG_COLORS: Record<ToolTag, string> = {
  readonly: "border-emerald-500/30 text-emerald-500",
  writeonly: "border-amber-500/30 text-amber-500",
  destructive: "border-rose-500/30 text-rose-500",
  expensive: "border-violet-500/30 text-violet-500",
  experimental: "border-sky-500/30 text-sky-500",
};

function prettyCategory(category: string): string {
  return category
    .split("-")
    .map((p) => p[0]?.toUpperCase() + p.slice(1))
    .join(" ");
}

export function IntegrationDetailPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { data: integration, isPending, error } = useQuery({
    queryKey: ["integration", slug],
    queryFn: () => integrationsApi.get(slug),
    enabled: !!slug,
    staleTime: 60_000,
  });
  const { data: creds } = useQuery({
    queryKey: ["credentials", "for", slug],
    queryFn: () => credentialsApi.list(slug),
    enabled: !!slug,
  });
  const credCount = creds?.length ?? 0;

  if (isPending) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (error || !integration) {
    return (
      <div className="mx-auto w-full max-w-5xl space-y-4 p-6">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/console/integrations">
            <ChevronLeft className="mr-1 h-4 w-4" /> Integrations
          </Link>
        </Button>
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
          {(error as Error | undefined)?.message ?? "Integration not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 p-6">
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to="/console/integrations">
          <ChevronLeft className="mr-1 h-4 w-4" /> Integrations
        </Link>
      </Button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <IntegrationLogo
          slug={integration.slug}
          src={integration.logo}
          name={integration.name}
          size={56}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              {integration.name}
            </h1>
            <Badge variant="outline" className="capitalize">
              {prettyCategory(integration.category)}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {integration.description}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            {integration.homepage && (
              <a
                href={integration.homepage}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 hover:text-foreground"
              >
                {new URL(integration.homepage).host}{" "}
                <ArrowUpRight className="h-3 w-3" />
              </a>
            )}
            {integration.docsUrl && (
              <a
                href={integration.docsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 hover:text-foreground"
              >
                API docs <ArrowUpRight className="h-3 w-3" />
              </a>
            )}
            <span>v{integration.version}</span>
          </div>
        </div>
        <Button asChild variant={credCount > 0 ? "outline" : "default"}>
          <Link to="/console/credentials">
            {credCount > 0 ? (
              <>
                <Check className="mr-2 h-4 w-4 text-emerald-500" />
                {credCount} connected · Manage
              </>
            ) : (
              <>
                <KeyRound className="mr-2 h-4 w-4" />
                Connect in Credentials
              </>
            )}
          </Link>
        </Button>
      </div>

      <Separator />

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-medium">
            Authentication ({integration.auth.length} method
            {integration.auth.length === 1 ? "" : "s"})
          </h2>
        </div>
        <ul className="space-y-2">
          {integration.auth.map((a, i) => (
            <li
              key={`${a.kind}-${a.provider ?? i}`}
              className="flex items-center gap-2 rounded-lg border bg-card p-4 text-sm"
            >
              <Badge variant="outline">{authLabel(a.kind)}</Badge>
              {a.provider && (
                <span className="text-muted-foreground">
                  provider <code className="font-mono">{a.provider}</code>
                </span>
              )}
              {a.tokenUrl && (
                <span className="text-muted-foreground">
                  token URL <code className="font-mono">{a.tokenUrl}</code>
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>

      <Tabs defaultValue="tools" className="w-full">
        <TabsList>
          <TabsTrigger value="tools">
            Tools ({integration.tools.length})
          </TabsTrigger>
          <TabsTrigger value="events">
            Events ({integration.events.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tools">
          <ul className="space-y-2">
            {integration.tools.map((tool) => (
              <li
                key={tool.name}
                className="rounded-lg border bg-card p-4"
              >
                <div className="flex items-start gap-3">
                  <code className="rounded bg-muted px-2 py-0.5 font-mono text-sm">
                    {integration.slug}.{tool.name}
                  </code>
                  {tool.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className={`text-[10px] uppercase ${TAG_COLORS[tag] ?? ""}`}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
                <p className="mt-2 text-sm font-medium">{tool.summary}</p>
                {tool.description && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {tool.description}
                  </p>
                )}
                {tool.requiresScope.length > 0 && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    <span className="font-medium">Scopes: </span>
                    {tool.requiresScope.map((s, i) => (
                      <span key={s}>
                        <code className="font-mono">{s}</code>
                        {i < tool.requiresScope.length - 1 ? ", " : ""}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </TabsContent>

        <TabsContent value="events">
          {integration.events.length === 0 ? (
            <div className="rounded-xl border border-dashed bg-muted/20 p-10 text-center text-sm text-muted-foreground">
              This integration doesn't emit events yet.
            </div>
          ) : (
            <>
              <p className="mb-3 text-xs text-muted-foreground">
                Inbound events the integration emits. Subscribe inside a
                loadout to react to them.
              </p>
              <ul className="space-y-2">
                {integration.events.map((event) => (
                  <li
                    key={event.name}
                    className="rounded-lg border bg-card p-4"
                  >
                    <div className="flex items-start gap-3">
                      <code className="rounded bg-muted px-2 py-0.5 font-mono text-sm">
                        {integration.slug}.{event.name}
                      </code>
                      {event.tags.map((tag) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="text-[10px] uppercase"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <p className="mt-2 text-sm font-medium">{event.summary}</p>
                    {event.description && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {event.description}
                      </p>
                    )}
                    {event.upstreamName && event.upstreamName.length > 0 && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        <span className="font-medium">Upstream: </span>
                        {event.upstreamName.map((n, i) => (
                          <span key={n}>
                            <code className="font-mono">{n}</code>
                            {i < event.upstreamName!.length - 1 ? ", " : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
